import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import {
  ChartLine, Lightning, Users, Warning, Clock, ArrowClockwise, ShieldWarning, Database, Trash
} from '@phosphor-icons/react';

const StatCard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-[#131313] border border-white/10 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider text-[#5C6B82] font-bold">{label}</span>
      <Icon size={18} weight="duotone" className={color} />
    </div>
    <p className="text-2xl font-black text-white">{value}</p>
    {sub && <p className="text-xs text-[#5C6B82] mt-1">{sub}</p>}
  </div>
);

const AnalyticsPage = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [recent, setRecent] = useState([]);
  const [rateLimitHits, setRateLimitHits] = useState(0);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [logStats, setLogStats] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('endpoints');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const opts = { withCredentials: true };
      const [sumRes, epRes, tlRes, recRes, rlRes, alertsRes, logStatsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/analytics/summary?days=${days}`, opts),
        axios.get(`${API_URL}/api/admin/analytics/endpoints?days=${days}`, opts),
        axios.get(`${API_URL}/api/admin/analytics/timeline?days=${days}`, opts),
        axios.get(`${API_URL}/api/admin/analytics/recent?limit=50`, opts),
        axios.get(`${API_URL}/api/admin/analytics/rate-limit-hits?days=${days}`, opts),
        axios.get(`${API_URL}/api/admin/analytics/security-alerts?limit=20`, opts),
        axios.get(`${API_URL}/api/admin/analytics/log-stats`, opts),
      ]);
      setSummary(sumRes.data);
      setEndpoints(epRes.data);
      setTimeline(tlRes.data);
      setRecent(recRes.data);
      setRateLimitHits(rlRes.data.rate_limited_requests);
      setSecurityAlerts(alertsRes.data);
      setLogStats(logStatsRes.data);
    } catch (e) {
      console.error('Analytics fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center pt-16">
        <p className="text-[#FF3B30] text-lg font-bold uppercase tracking-wider" data-testid="analytics-no-access">
          Admin access required
        </p>
      </div>
    );
  }

  const maxTimelineCount = Math.max(...timeline.map(t => t.count), 1);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-4" data-testid="analytics-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight">
              API Analytics
            </h1>
            <p className="text-sm text-[#5C6B82] mt-1">Usage & rate limiting dashboard</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              className="bg-[#131313] border border-white/10 text-[#CCD6F6] text-sm px-3 py-2 focus:outline-none focus:border-[#007AFF]"
              data-testid="analytics-days-select"
            >
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <button
              onClick={fetchData}
              className="p-2 text-[#8892B0] hover:text-white transition-colors"
              data-testid="analytics-refresh"
            >
              <ArrowClockwise size={20} weight="bold" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <StatCard
                label="Total Requests"
                value={summary?.total_requests?.toLocaleString() || '0'}
                sub={`Last ${days} day${days > 1 ? 's' : ''}`}
                icon={ChartLine}
                color="text-[#007AFF]"
              />
              <StatCard
                label="Unique Visitors"
                value={summary?.unique_visitors?.toLocaleString() || '0'}
                sub="By IP address"
                icon={Users}
                color="text-[#30D158]"
              />
              <StatCard
                label="Avg Response"
                value={`${summary?.avg_response_ms || 0}ms`}
                sub="Across all endpoints"
                icon={Lightning}
                color="text-[#FF9F0A]"
              />
              <StatCard
                label="Error Rate"
                value={`${summary?.error_rate || 0}%`}
                sub={`${summary?.error_count || 0} errors`}
                icon={Warning}
                color="text-[#FF3B30]"
              />
              <StatCard
                label="Rate Limited"
                value={rateLimitHits.toLocaleString()}
                sub="429 responses"
                icon={Clock}
                color="text-[#BF5AF2]"
              />
            </div>

            {/* Timeline Bar Chart */}
            {timeline.length > 0 && (
              <div className="bg-[#131313] border border-white/10 p-5 mb-8" data-testid="analytics-timeline">
                <h2 className="text-xs uppercase tracking-wider text-[#5C6B82] font-bold mb-4">
                  Request Volume
                </h2>
                <div className="flex items-end gap-[2px] h-32 overflow-x-auto">
                  {timeline.map((t) => {
                    const pct = (t.count / maxTimelineCount) * 100;
                    return (
                      <div
                        key={t.label}
                        className="group relative flex-1 min-w-[4px] flex flex-col items-center justify-end h-full"
                      >
                        <div
                          className="w-full bg-[#007AFF]/80 hover:bg-[#007AFF] transition-colors rounded-t-sm"
                          style={{ height: `${Math.max(pct, 2)}%` }}
                        />
                        <div className="absolute bottom-full mb-1 hidden group-hover:block bg-[#1a1a1a] border border-white/10 text-[10px] text-[#CCD6F6] px-2 py-1 whitespace-nowrap z-10">
                          {t.label}: {t.count} req, {t.errors} err
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-4 flex-wrap">
              {['endpoints', 'recent', 'security', 'storage'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${
                    activeTab === tab
                      ? 'bg-[#007AFF] text-white'
                      : 'bg-[#131313] text-[#5C6B82] hover:text-white border border-white/10'
                  }`}
                  data-testid={`analytics-tab-${tab}`}
                >
                  {tab === 'endpoints' ? 'Top Endpoints' :
                   tab === 'recent' ? 'Recent Requests' :
                   tab === 'security' ? `Security Alerts${securityAlerts.length ? ` (${securityAlerts.length})` : ''}` :
                   'Log Storage'}
                </button>
              ))}
            </div>

            {/* Top Endpoints Table */}
            {activeTab === 'endpoints' && (
              <div className="bg-[#131313] border border-white/10 overflow-x-auto" data-testid="analytics-endpoints-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-[#5C6B82]">
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold">Endpoint</th>
                      <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold">Hits</th>
                      <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold">Avg (ms)</th>
                      <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoints.map((ep) => (
                      <tr key={ep.endpoint} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3 font-mono text-[#CCD6F6] text-xs">{ep.endpoint}</td>
                        <td className="px-4 py-3 text-right text-white font-bold">{ep.count.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-[#8892B0]">{ep.avg_ms}</td>
                        <td className={`px-4 py-3 text-right font-bold ${ep.errors > 0 ? 'text-[#FF3B30]' : 'text-[#30D158]'}`}>
                          {ep.errors}
                        </td>
                      </tr>
                    ))}
                    {endpoints.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5C6B82]">No data yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recent Requests Table */}
            {activeTab === 'recent' && (
              <div className="bg-[#131313] border border-white/10 overflow-x-auto" data-testid="analytics-recent-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-[#5C6B82]">
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold">Time</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold">Method</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold">Path</th>
                      <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold">Status</th>
                      <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold">Time (ms)</th>
                      <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((r) => (
                      <tr key={`${r.ts}-${r.path}-${r.method}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-2 text-xs text-[#5C6B82] whitespace-nowrap">
                          {r.ts ? new Date(r.ts).toLocaleTimeString() : '-'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`text-xs font-bold px-2 py-0.5 ${
                            r.method === 'GET' ? 'bg-[#30D158]/10 text-[#30D158]' :
                            r.method === 'POST' ? 'bg-[#007AFF]/10 text-[#007AFF]' :
                            r.method === 'PUT' ? 'bg-[#FF9F0A]/10 text-[#FF9F0A]' :
                            'bg-[#FF3B30]/10 text-[#FF3B30]'
                          }`}>
                            {r.method}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-[#CCD6F6] max-w-[300px] truncate">{r.path}</td>
                        <td className={`px-4 py-2 text-right font-bold text-xs ${
                          r.status < 300 ? 'text-[#30D158]' :
                          r.status < 400 ? 'text-[#FF9F0A]' :
                          'text-[#FF3B30]'
                        }`}>
                          {r.status}
                        </td>
                        <td className="px-4 py-2 text-right text-xs text-[#8892B0]">{r.response_ms}</td>
                        <td className="px-4 py-2 text-xs text-[#5C6B82] font-mono">{r.ip}</td>
                      </tr>
                    ))}
                    {recent.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C6B82]">No data yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Security Alerts */}
            {activeTab === 'security' && (
              <div data-testid="analytics-security-alerts">
                {securityAlerts.length === 0 ? (
                  <div className="bg-[#131313] border border-white/10 p-8 text-center">
                    <ShieldWarning size={40} className="text-[#30D158] mx-auto mb-3" weight="duotone" />
                    <p className="text-[#CCD6F6] font-bold">No security alerts</p>
                    <p className="text-xs text-[#5C6B82] mt-1">
                      Alerts trigger when an IP is rate-limited 10+ times in 5 minutes
                    </p>
                  </div>
                ) : (
                  <div className="bg-[#131313] border border-white/10 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10 text-[#5C6B82]">
                          <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold">Time</th>
                          <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold">IP Address</th>
                          <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold">Type</th>
                          <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold">429 Hits</th>
                          <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold">Window</th>
                        </tr>
                      </thead>
                      <tbody>
                        {securityAlerts.map((a) => (
                          <tr key={`${a.ip}-${a.created_at}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                            <td className="px-4 py-3 text-xs text-[#5C6B82] whitespace-nowrap">
                              {a.created_at ? new Date(a.created_at).toLocaleString() : '-'}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-[#FF9F0A] font-bold">{a.ip}</td>
                            <td className="px-4 py-3">
                              <span className="text-xs font-bold px-2 py-0.5 bg-[#FF3B30]/10 text-[#FF3B30] uppercase">
                                {a.type}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-white font-bold">{a.hits_429}</td>
                            <td className="px-4 py-3 text-right text-xs text-[#8892B0]">{a.window_minutes}min</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Log Storage */}
            {activeTab === 'storage' && logStats && (
              <div className="space-y-4" data-testid="analytics-log-storage">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard
                    label="Total Logs"
                    value={logStats.total_logs?.toLocaleString() || '0'}
                    sub={`Retention: ${logStats.retention_days} days (auto-cleanup)`}
                    icon={Database}
                    color="text-[#007AFF]"
                  />
                  <StatCard
                    label="Security Alerts"
                    value={logStats.total_security_alerts?.toLocaleString() || '0'}
                    sub="Brute-force detections"
                    icon={ShieldWarning}
                    color={logStats.total_security_alerts > 0 ? 'text-[#FF3B30]' : 'text-[#30D158]'}
                  />
                  <StatCard
                    label="Log Range"
                    value={logStats.oldest_log ? `${Math.ceil((new Date(logStats.newest_log) - new Date(logStats.oldest_log)) / 86400000)}d` : '0d'}
                    sub={logStats.oldest_log ? `${new Date(logStats.oldest_log).toLocaleDateString()} - ${new Date(logStats.newest_log).toLocaleDateString()}` : 'No logs'}
                    icon={Clock}
                    color="text-[#BF5AF2]"
                  />
                </div>
                <div className="bg-[#131313] border border-white/10 p-5">
                  <h3 className="text-xs uppercase tracking-wider text-[#5C6B82] font-bold mb-3">Auto-Cleanup</h3>
                  <p className="text-sm text-[#8892B0]">
                    Logs older than <span className="text-white font-bold">{logStats.retention_days} days</span> are automatically
                    deleted by MongoDB TTL index. No manual maintenance required.
                  </p>
                  <button
                    onClick={async () => {
                      if (window.confirm('Purge logs older than 30 days? This cannot be undone.')) {
                        try {
                          const res = await axios.delete(`${API_URL}/api/admin/analytics/purge-logs?older_than_days=30`, { withCredentials: true });
                          alert(`Purged ${res.data.deleted} old log entries`);
                          fetchData();
                        } catch (e) {
                          alert('Purge failed');
                        }
                      }
                    }}
                    className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#FF3B30]/10 text-[#FF3B30] text-xs uppercase tracking-wider font-bold hover:bg-[#FF3B30]/20 transition-colors"
                    data-testid="purge-logs-btn"
                  >
                    <Trash size={14} weight="bold" />
                    Manual Purge
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
